import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShowroundCalendar } from './use-showround-calendar';
import { useCalendarSelection } from './use-calendar-selection';
import { useShowroundBooking, CreateBookingAnswer } from './use-showround-booking';
import { ShowroundFormData } from './use-showround-form-schema';

import { useSelector } from '@bridebook/web/lib/utils';
import { getShowroundSupplierContextTimezone } from '@bridebook/web/lib/showrounds/state/selectors';

interface UseShowroundManagerOptions {
  supplierId: string;
  enabled?: boolean;
  initialEventId?: string;
}

/**
 * Main hook for managing showround booking flow state and actions.
 *
 * Combines calendar data, user selections, and booking functionality into a single interface.
 * Used via ShowroundContext to provide centralized state management across showround components.
 *
 * @param options - Configuration options
 * @param options.supplierId - ID of the supplier to fetch calendar data for
 * @param options.enabled - Whether to enable data fetching (default: true)
 * @param options.initialEventId - Optional initial event ID to select
 *
 * @returns Object containing:
 * - showroundCalendar: Calendar configuration and metadata for showround
 * - openDays: Open-day events available alongside the private showround flow
 * - hasAvailability: Whether any time slots are available
 * - isLoading: Loading state for calendar data
 * - selection: Date/time selection state and actions (selectDate, selectSlot, clearSelection)
 * - booking: Booking state and actions including bookSelectedSlot function
 */
export const useShowroundManager = (options: UseShowroundManagerOptions) => {
  const { supplierId, enabled = true, initialEventId } = options;
  const timezone = useSelector(getShowroundSupplierContextTimezone);
  const [browsedCalendarDate, setBrowsedCalendarDate] = useState<Date | string | null>(null);

  const { showroundCalendar, slots, openDays, isLoading, hasAvailability } = useShowroundCalendar(
    supplierId,
    {
      enabled,
    },
  );

  const selection = useCalendarSelection(slots, timezone);
  const slotsAvailable = slots.length;
  const [eventId, setEventId] = useState<string | undefined>(initialEventId);

  const selectedOpenDay = useMemo(
    () => (eventId ? openDays?.find((d) => d.id === eventId) : undefined),
    [eventId, openDays],
  );

  const effectiveCalendarId = selectedOpenDay?.calendarId || showroundCalendar?.id || '';
  const booking = useShowroundBooking(effectiveCalendarId, Boolean(selectedOpenDay));

  const bookSelectedSlot = useCallback(
    async (formData: ShowroundFormData, questionAnswers: CreateBookingAnswer[] = []) => {
      if (!selection.selectedSlot && !eventId) {
        return { success: false, errors: { general: 'No slot selected' } };
      }

      const completeFormData = {
        ...formData,
        selectedDate: (selection.selectedDate as string) || '',
        selectedSlot: (selection.selectedSlot as string) || '',
      };

      const result = await booking.bookShowround(completeFormData, questionAnswers, eventId);

      if (result.success) {
        selection.clearSelection();
      }

      return result;
    },
    [selection, booking, eventId],
  );

  // Listen to the selected date and set the browsed calendar date
  useEffect(() => {
    setBrowsedCalendarDate(selection.selectedDate);
  }, [selection.selectedDate]);

  return {
    showroundCalendar,
    slots,
    hasAvailability,
    openDays,
    isLoading,
    selection,
    slotsAvailable,
    browsedCalendarDate,
    setBrowsedCalendarDate,
    eventId,
    selectedOpenDay,
    setEventId,
    booking: {
      ...booking,
      bookSelectedSlot,
    },
  };
};

export type ShowroundManager = ReturnType<typeof useShowroundManager>;
